/*
  Click-to-load facades for video.

  On video-heavy builder pages, initializing a real Video.js player for every video exhausts
  mobile memory and leaves piles of detached DOM nodes — crashing the browser. The server
  (AssetRenderer#render_video_facade) instead renders a lightweight poster + play button, with
  the real <video> stashed in an inert <template>. Here we promote a facade into a real player
  only when needed, and keep at most one live player at a time by disposing + reverting the
  others. No scroll-driven create/destroy churn, so no flicker.

  index.js decides per page (see the >6 gate) whether facades stay facades (promote on tap) or
  fall back to the previous lazy-on-scroll behavior (promote on viewport entry).
*/

import { onMobileAppChromecastLoadedIfAvailable } from './custom_plugins/mobile_app_chromecast'

const FACADE_SELECTOR = '.video-facade'

// Wrappers that currently host a live player promoted from a facade. Used to enforce the
// "at most one live player" rule and to revert them back to their poster on demand.
const promoted = new Set()

// Whether promoting one facade should dispose the others. True on many-video pages (tap to play,
// one at a time); false on few-video pages where we keep every player alive like before.
let disposeOnPromote = false
export const setDisposeOnPromote = (value) => {
  disposeOnPromote = value
}

const revertToFacade = (wrapper) => {
  const player = wrapper._videojsPlayer
  try {
    if (player && !player.isDisposed()) player.dispose()
    // Player not initialized yet (bundle still loading): the cloned <video> may already be
    // playing from the synchronous play() in promote(). Detaching via outerHTML doesn't pause it,
    // so pause directly to avoid phantom audio.
    else if (wrapper._promotedVideo) wrapper._promotedVideo.pause()
  } catch (e) {
    // ignore — a half-disposed player still gets replaced by the facade markup below
  }
  promoted.delete(wrapper)
  if (wrapper._facadeSnapshot) wrapper.outerHTML = wrapper._facadeSnapshot
}

const disposeAndRevertOthers = (current) => {
  ;[...promoted].forEach((wrapper) => {
    if (wrapper !== current) revertToFacade(wrapper)
  })
}

// Replace the facade with the real <video> from the <template> and initialize Video.js on it.
// `play` triggers playback after init (only for a genuine user tap, so unmuted play is allowed).
const promote = (wrapper, { play = false } = {}) => {
  if (!wrapper.matches(FACADE_SELECTOR)) return

  if (disposeOnPromote) disposeAndRevertOthers(wrapper)

  // Capture the facade markup so we can revert to it (and so the Turbo bfcache snapshot in
  // main.js restores a facade rather than a live player — which would leak on page restore).
  wrapper._facadeSnapshot = wrapper.outerHTML

  const template = wrapper.querySelector('template.video-facade__template')
  const video = template.content.querySelector('video').cloneNode(true)

  wrapper.replaceChildren(video)
  wrapper._promotedVideo = video
  wrapper.classList.remove('video-facade')
  wrapper.removeAttribute('data-facade')
  promoted.add(wrapper)

  // Kick off playback SYNCHRONOUSLY, still inside the user gesture, so mobile browsers (esp. iOS
  // Safari — which requires play() in the gesture's call stack) allow unmuted play. Without this,
  // play() ran only after `await import()` below, outside the activation window, so it got blocked
  // and Video.js showed its big-play-button — the "extra click". Video.js then attaches to this
  // already-playing element and keeps it going. The ready()-fallback below covers MSE sources
  // (dash) where native play can't start the stream but Video.js still has transient activation.
  if (play) {
    const started = video.play()
    // Swallow rejection — on MSE-only sources native play can't start; Video.js handles it below.
    if (started && started.catch) started.catch(() => undefined)
  }

  onMobileAppChromecastLoadedIfAvailable(async () => {
    const { initializeVideoPlayer } = await import('./main.js')
    // Wrapper was reverted while the bundle loaded (user tapped another tile): don't initialize a
    // detached player. revertToFacade already paused the cloned <video>.
    if (!document.contains(wrapper)) return

    const player = initializeVideoPlayer(video)
    wrapper._videojsPlayer = player
    if (play && player) player.ready(() => player.play())
  })
}

// Few-video pages: keep the previous lazy-on-scroll behavior — initialize (but don't auto-play
// or dispose others) when the facade approaches the viewport.
const observeFacade = (wrapper) => {
  if (!('IntersectionObserver' in window)) {
    promote(wrapper)
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect()
        promote(wrapper)
      }
    },
    { rootMargin: '400px' }
  )
  observer.observe(wrapper)
}

export const lazyInitFacade = observeFacade

// Drop references to wrappers from a previous page (Turbo nav / bfcache restore). The players
// themselves are disposed by the page:before-unload handler in main.js.
export const resetFacades = () => promoted.clear()

// Delegated so reverted facades (whose DOM nodes were re-parsed) keep working without rebinding.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.video-facade__btn')
  if (!btn) return

  e.preventDefault()
  const wrapper = btn.closest(FACADE_SELECTOR)
  if (wrapper) promote(wrapper, { play: true })
})
